import React from 'react';
import { useDocumentMeta } from './useDocumentMeta';
import { BOOKING_URL } from './siteConfig';
import './CaseStudy.css';

const CaseStudy: React.FC = () => {
  useDocumentMeta(
    'Preach Records Transforms Music Marketing Operations with Cadence IO',
    'Discover how this independent label eliminated about 80 hours of annual manual work and avoided a full-time hire by automating their DSP submission workflow.'
  );

  return (
    <div className="case-study-page">
      {/* Hero Section */}
      <section className="case-study-hero">
        <div className="case-study-hero-content">
          <div className="hero-text">
            <h1>
              Preach Records Transforms Music Marketing Operations 
              <span className="highlight"> with Cadence IO</span>
            </h1>
            <p>Discover how this independent label eliminated about 80 hours of annual manual work and avoided a full-time hire by automating their DSP submission workflow.</p>
            <div className="hero-stats">
              <div className="hero-stat">
                <div className="stat-number">80 hrs</div>
                <div className="stat-label">Annual time saved</div>
              </div>
              <div className="hero-stat">
                <div className="stat-number">7→1</div>
                <div className="stat-label">Forms to submission</div>
              </div>
            </div>
          </div>
          <div className="hero-image">
            <img 
              src={require('./Images/Preach-Records-Logo.png')} // You'll need to add this image
              alt="Preach Records Team" 
            />
          </div>
        </div>
      </section>

      {/* Challenge Section - Fixed Layout */}
      <section className="challenge-section">
        <h2 className="section-title" style={{ color: '#6D6E71' }}>Challenge</h2>
        <div className="challenge-content">
          <div className="challenge-text">
            <p>Preach Records, an independent label, was struggling with inefficient DSP submission workflows. The small team was spending valuable time manually submitting each release to multiple streaming platforms including Spotify, Amazon, Deezer, Audiomack, Tidal, and SoundCloud.</p>
            
            <p>With anywhere from 0-3 releases per week, the team was dedicating at least 1.5 hours on average weekly to repetitive data entry across different DSP portals. The repetitiveness of filling out the same information across multiple platforms was creating a significant operational burden.</p>
            
            <p>This operational inefficiency had real consequences - time spent on manual submissions directly reduced hours available for creative marketing, artist meetings, and strategic content development. For a small, agile team, these 1.5-2 hours represented critical creative capacity being diverted to administrative tasks.</p>
            
            <p>As Preach Records planned to expand their roster and take on more artists, they were faced with the prospect of hiring a full-time operations employee to manage the increasing submission workload. This additional headcount would represent a substantial investment in salary, benefits, and management resources.</p>
          </div>
          <div className="challenge-right">
            <div className="challenge-image">
              <img 
                src={require('./Images/Manual-Pitch-Process.gif')} // You can create or find a relevant image
                alt="Manual DSP Submission Process" 
              />
            </div>
            
            <div className="challenge-points">
              <div className="challenge-point">
                <div className="point-icon">⏰</div>
                <div className="point-content">
                  <strong>Time-Intensive Process:</strong> At least 1.5 hours weekly spent on repetitive data entry across different DSP portals
                </div>
              </div>
              <div className="challenge-point">
                <div className="point-icon">🔄</div>
                <div className="point-content">
                  <strong>Repetitive Work:</strong> Filling out the same information across 7+ different platform forms for each release
                </div>
              </div>
              <div className="challenge-point">
                <div className="point-icon">👥</div>
                <div className="point-content">
                  <strong>Scaling Challenges:</strong> Planning to hire full-time operations support as roster expanded
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="solution-section">
        <h2 className="section-title" style={{ color: '#6D6E71' }}>Solution</h2>
        <div className="solution-content">
          <p>Preach Records implemented Cadence IO to streamline and automate their DSP marketing submission process. The platform allowed them to:</p>
          
          <div className="solution-steps">
            <div className="solution-step">
              <div className="step-number">1</div>
              <h4>Single Entry Point</h4>
              <p>Enter marketing information once through a single unified form</p>
            </div>
            <div className="solution-step">
              <div className="step-number">2</div>
              <h4>Automatic Distribution</h4>
              <p>Automatically distribute standardized, high-quality data to multiple DSPs</p>
            </div>
            <div className="solution-step">
              <div className="step-number">3</div>
              <h4>Consistent Quality</h4>
              <p>Maintain consistent submission quality across all platforms</p>
            </div>
            <div className="solution-step">
              <div className="step-number">4</div>
              <h4>Expanded Reach</h4>
              <p>Expand their pitching reach to previously underutilized DSPs</p>
            </div>
          </div>
          
          <p>With Cadence IO handling the operational workflow, the Preach Records team could redirect their energy toward strategic marketing initiatives and creative artist development, while also avoiding the need for additional operational headcount as they scaled.</p>
        </div>
      </section>

      {/* Results Section */}
      <section className="results-section">
        <h2 className="section-title" style={{ color: '#6D6E71' }}>Results</h2>

        <div className="results-badges">
          <div className="result-badge">
            <div className="badge-number">80 hrs</div>
            <div className="badge-label">Annual time saved</div>
          </div>
          <div className="result-badge">
            <div className="badge-label badge-label-only">Avoided an operations hire</div>
          </div>
          <div className="result-badge">
            <div className="badge-number">1→5</div>
            <div className="badge-label">Platforms per release</div>
          </div>
        </div>

        <div className="results-grid">
          <div className="result-card">
            <h3>Coverage</h3>
            <p>Before Cadence, Preach releases went to Spotify.</p>
            <p>Now every release goes to five: Spotify, Amazon, Deezer, Tidal, and SoundCloud. Apple Music is not among them, because Apple grants pitch access to a limited set of labels and distributors and Preach is not one of them.</p>
          </div>

          <div className="result-card">
            <h3>Time</h3>
            <p>The submission work ran about an hour and a half a week, or roughly 80 hours a year. That time now goes to content and campaign work.</p>
          </div>

          <div className="result-card">
            <h3>Avoided an operations hire</h3>
            <p>Preach had been planning to add a full-time operations person as the roster grew. A few months into using Cadence they decided the hire was not needed. At a $70,000 base and a 1.35 loaded multiplier, that role would have cost about $94,500 a year.</p>
          </div>
        </div>
      </section>

      {/* Scalability Section */}
      <section className="scalability-section">
        <h2 className="section-title" style={{ color: '#6D6E71' }}>Scalability</h2>
        <p>Preach puts out zero to three releases a week, which worked out to roughly 78 to 234 hours a year of submission work. A label doing four to nine releases a week is looking at 312 to 702 hours on the same task.</p>
      </section>

      {/* Business Impact */}
      <section className="business-impact">
        <h2 className="section-title" style={{ color: '#6D6E71' }}>Business Impact</h2>
        <div className="impact-content">
          <div className="impact-text">
            <p>For Preach Records, Cadence IO has transformed a time-consuming operational burden into a streamlined, efficient process. The platform delivers four core benefits:</p>
            
            <div className="impact-benefits">
              <div className="benefit-item">
                <strong>1. Operational efficiency:</strong> Eliminating about 80 hours of manual work annually
              </div>
              <div className="benefit-item">
                <strong>2. Significant cost savings:</strong> Avoiding the need for a full-time operations hire as the roster expanded
              </div>
              <div className="benefit-item">
                <strong>3. Enhanced marketing coverage:</strong> Consistent submissions across all major DSPs
              </div>
              <div className="benefit-item">
                <strong>4. Creative capacity:</strong> Redirecting valuable team resources to high-impact marketing initiatives
              </div>
            </div>
            
            <p>The avoided headcount is the clearest return on using Cadence IO. It let Preach Records scale their operations and artist roster without proportionally increasing their operational costs.</p>
            
            <p>By addressing the critical operational bottleneck of DSP submissions, Cadence IO enables labels like Preach Records to focus on what truly matters - breaking artists and creating compelling marketing campaigns, rather than being bogged down by repetitive administrative tasks or having to expand headcount to manage growth.</p>
          </div>
          <div className="impact-visual">
            <div className="before-after">
              <div className="before">
                <h4>Before Cadence IO</h4>
                <ul>
                  <li>7+ manual forms per release</li>
                  <li>At least 1.5 hours weekly on submissions</li>
                  <li>Planning operational hire</li>
                  <li>Limited DSP coverage</li>
                </ul>
              </div>
              <div className="after">
                <h4>After Cadence IO</h4>
                <ul>
                  <li>1 unified submission form</li>
                  <li>Time redirected to creative work</li>
                  <li>No additional hire needed</li>
                  <li>Comprehensive DSP coverage</li>
                </ul>
              </div>
            </div>
            
            <div className="client-quote">
              <blockquote>
                "It's a no-brainer that cuts down work time tremendously when pitching to DSPs."
              </blockquote>
              <cite>- Preach Records Team</cite>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Fixed button styling */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready to Transform Your Label Operations?</h2>
          <p>Join Preach Records and other forward-thinking labels who have eliminated operational bottlenecks with Cadence IO.</p>
          <div className="cta-buttons">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Book a call
            </a>
            <a href="/about" className="btn-secondary">
              <span style={{color: 'white'}}>Learn More</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudy;